import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { Entity } from '../types';
import { ContextManager } from './manager';
import { ContextSetting } from './types';

type EditContext = {
  getContextSettings: (names: string[]) => Promise<Record<string, ContextSetting>>;
  updateSettingsValues: (updates: Record<string, Entity[]>, isLiveView?: boolean, skipRefetch?: boolean) => void;
  clearSettingOptions: (names: string[]) => void;
};

const editContext = createContext<EditContext>({
  getContextSettings: () => Promise.resolve({}),
  updateSettingsValues: () => {},
  clearSettingOptions: () => {},
});

type Props = {
  children: ReactNode;
};
function EditProvider({ children }: Props) {
  const getContextSettings = (names: string[]): Promise<Record<string, ContextSetting>> => {
    return ContextManager.getInstance().getSettings(names);
  };

  const updateSettingsValues = (
    updates: Record<string, Entity[]>,
    isLiveView?: boolean,
    skipRefetch?: boolean
  ): void => {
    ContextManager.getInstance().updateSettingsValues(updates, isLiveView, skipRefetch);
  };

  const clearSettingOptions = (names: string[]): void => {
    return ContextManager.getInstance().clearSettingOptions(names);
  };

  const value = useMemo(
    () => ({
      getContextSettings,
      updateSettingsValues,
      clearSettingOptions,
    }),
    []
  );

  return <editContext.Provider value={value}>{children}</editContext.Provider>;
}

function useEditContextSettings() {
  const context = useContext(editContext);
  if (!context) {
    throw new Error('useEditContextSettings must be used within a provider');
  }
  return context;
}

export { EditProvider, useEditContextSettings };
